import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wifi, Router, ChevronLeft } from 'lucide-react';
import { useWirelessAdvisor } from './context/WirelessAdvisorContext';
import { selectionModalVariants, contentVariants, stepTransitionVariants } from './utils/animations';
import { FORM_STEPS } from './utils/constants';

// Form step components
import HomeSize from './components/FormSteps/HomeSize';
import RouterLocation from './components/FormSteps/RouterLocation';
import Rooms from './components/FormSteps/Rooms';
import Layout from './components/FormSteps/Layout';
import Coverage from './components/FormSteps/Coverage';
import InternetSpeed from './components/FormSteps/InternetSpeed';
import StepIndicator from './components/shared/StepIndicator';
import LoadingIndicator from './components/shared/LoadingIndicator';

/**
 * SelectionModal component
 * 1/4 width modal that slides in from the right
 * Contains the multi-step form for wireless advisor
 */
const SelectionModal = ({
  isOpen,
  onClose
}) => {
  const {
    step,
    setStep,
    nextStep,
    prevStep,
    formData,
    updateFormData,
    calculateAndShowResults, // Use the renamed function from context
    isCurrentStepComplete,
    direction,
    currentStepIndex
  } = useWirelessAdvisor();
  
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore scrolling when component unmounts or modal closes
        document.body.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  // Render the current step component
  const renderStep = () => {
    switch (step) {
      case 'size':
        return <HomeSize />;
      case 'routerLocation':
        return <RouterLocation />;
      case 'rooms':
        return <Rooms />;
      case 'layout':
        return <Layout />;
      case 'coverage':
        return <Coverage />;
      case 'internetSpeed':
        return <InternetSpeed />;
      case 'generating':
        return <LoadingIndicator message="Analyzing your home and calculating recommendations..." />;
      default:
        return null;
    }
  };
  
  // Get step title
  const getStepTitle = () => {
    switch (step) {
      case 'size':
        return 'Home Size';
      case 'routerLocation':
        return 'Router Location';
      case 'rooms':
        return 'Room Layout';
      case 'layout':
        return 'Home Details';
      case 'coverage':
        return 'Coverage Areas';
      case 'internetSpeed':
        return 'Internet Speed';
      case 'generating':
        return 'Generating Recommendation';
      default:
        return 'Wireless Advisor';
    }
  };
  
  // Handle next button click
  const handleNext = () => {
    if (step === 'internetSpeed') {
      // If we're on the last step, calculate results using the correct function
      calculateAndShowResults();
    } else {
      // Otherwise, go to next step
      nextStep();
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - only on mobile */}
          {isMobile && (
            <motion.div
              className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />
          )}
          
          {/* Modal */}
          <motion.div
            className={`fixed top-0 bottom-0 right-0 bg-gradient-to-b from-gray-900 to-gray-800 shadow-2xl border-l border-gray-800 flex flex-col z-50 ${
              isMobile ? 'w-full' : 'w-1/4'
            }`}
            variants={selectionModalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <motion.div 
              className="p-4 border-b border-gray-800 flex justify-between items-center"
              variants={contentVariants}
            >
              <div className="flex items-center">
                <Router className="h-5 w-5 text-purple-400 mr-2" />
                <h2 className="text-xl font-bold text-white">{getStepTitle()}</h2>
              </div>
              <button 
                className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800/50 transition-colors"
                onClick={onClose}
              >
                <X className="h-5 w-5" />
              </button>
            </motion.div>
            
            {/* Step Indicator */}
            <motion.div
              className="px-4 py-2 border-b border-gray-800 bg-gray-800/50"
              variants={contentVariants}
            >
              <StepIndicator 
                steps={FORM_STEPS.filter(s => s !== 'generating' && s !== 'results')}
                currentStep={step}
                onStepClick={(stepName) => {
                  // Only allow clicking on completed steps
                  const stepIndex = FORM_STEPS.indexOf(stepName);
                  if (stepIndex <= currentStepIndex) {
                    setStep(stepName);
                  }
                }}
              />
            </motion.div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={step}
                  custom={direction}
                  variants={stepTransitionVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="p-4"
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Footer with navigation buttons */}
            <motion.div
              className="p-4 border-t border-gray-800 flex justify-between"
              variants={contentVariants}
            >
              {currentStepIndex > 0 && step !== 'generating' ? (
                <button
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center"
                  onClick={prevStep}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </button>
              ) : (
                <div></div> // Empty div to maintain layout with flex justify-between
              )}
              
              {step !== 'generating' && (
                <button
                  className={`px-6 py-2 rounded-lg font-medium ${
                    isCurrentStepComplete()
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                      : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  }`}
                  onClick={handleNext}
                  disabled={!isCurrentStepComplete()}
                >
                  {step === 'internetSpeed' ? 'Calculate' : 'Next'}
                </button>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SelectionModal;